import React, { useId, useState } from "react"

import * as Headless from "@headlessui/react"
import { ChevronDownIcon } from "@heroicons/react/16/solid"
import clsx from "clsx"

import { CONTROL_HEIGHT_CLASS } from "./control"
import { Description, ErrorMessage, Field, Label } from "./fieldset"

export type ComboboxFieldProps<T> = {
  label?: React.ReactNode
  labelSrOnly?: boolean
  description?: React.ReactNode
  error?: React.ReactNode
  disabled?: boolean
  className?: string
  placeholder?: string
  name?: string
  options: T[]
  value: T | null
  onChange: (value: T | null) => void
  getKey: (option: T) => string | number
  getLabel: (option: T) => string
  filter?: (option: T, query: string) => boolean
  renderOption?: (option: T) => React.ReactNode
  emptyMessage?: React.ReactNode
  immediate?: boolean
  onQueryChange?: (query: string) => void
  portal?: boolean
}

const IS_DEV_BUILD = process.env.NODE_ENV !== `production`

export function ComboboxField<T>({
  label,
  labelSrOnly = false,
  description,
  error,
  disabled,
  className,
  placeholder,
  name,
  options,
  value,
  onChange,
  getKey,
  getLabel,
  filter,
  renderOption,
  emptyMessage = `No results.`,
  immediate,
  onQueryChange,
  portal = true,
}: ComboboxFieldProps<T>) {
  const errorId = useId()
  const errorMessageId = error ? errorId : undefined
  const [query, setQuery] = useState(``)

  if (IS_DEV_BUILD && !label && !labelSrOnly) {
    // eslint-disable-next-line no-console
    console.error(
      `<ComboboxField> requires a 'label' prop, or 'labelSrOnly' must be true to acknowledge the field is intentionally unlabelled.`,
    )
  }

  const filtered =
    query === ``
      ? options
      : options.filter((option) =>
          filter
            ? filter(option, query)
            : getLabel(option).toLowerCase().includes(query.toLowerCase()),
        )

  return (
    <Field className={className} disabled={disabled}>
      {label ? (
        <Label
          className={labelSrOnly ? `sr-only` : `text-sm font-medium text-muted`}
        >
          {label}
        </Label>
      ) : null}
      {description ? <Description>{description}</Description> : null}
      <Headless.Combobox
        as="div"
        data-slot="control"
        value={value}
        onChange={(next: T | null) => {
          setQuery(``)
          onQueryChange?.(``)
          onChange(next)
        }}
        onClose={() => {
          setQuery(``)
          onQueryChange?.(``)
        }}
        disabled={disabled}
        name={name}
        multiple={false}
        immediate={immediate}
      >
        <span
          className={clsx(
            `relative block w-full`,
            `has-data-disabled:cursor-not-allowed has-data-disabled:opacity-50`,
          )}
        >
          <Headless.ComboboxInput
            aria-invalid={error ? true : undefined}
            aria-errormessage={errorMessageId}
            data-invalid={error ? true : undefined}
            displayValue={(option: T | null) =>
              option === null ? `` : getLabel(option)
            }
            onChange={(event) => {
              setQuery(event.target.value)
              onQueryChange?.(event.target.value)
            }}
            placeholder={placeholder}
            className={clsx(
              CONTROL_HEIGHT_CLASS,
              `relative block w-full appearance-none rounded-md`,
              `border border-control-border bg-background py-0 pr-12 pl-2.5 shadow-sm`,
              `text-base/6 text-foreground placeholder:text-muted sm:text-sm/6`,
              `data-hover:border-primary`,
              `focus:outline-hidden`,
              `data-focus:border-primary data-focus:ring-2 data-focus:ring-focus-ring/25`,
              `data-invalid:border-danger data-hover:data-invalid:border-danger data-focus:data-invalid:ring-danger/20`,
              `data-disabled:cursor-not-allowed data-disabled:border-control-border data-disabled:bg-surface data-hover:data-disabled:border-control-border`,
            )}
          />
          <Headless.ComboboxButton
            data-slot="combobox-toggle"
            className="group absolute inset-y-0 right-0 flex items-center pr-2.5"
          >
            <span className="flex h-1/2 items-center border-l border-border pl-2.5 text-muted">
              <ChevronDownIcon
                aria-hidden="true"
                className="size-4 transition-transform group-data-open:rotate-180"
              />
            </span>
          </Headless.ComboboxButton>

          <Headless.ComboboxOptions
            transition
            portal={portal}
            modal={portal}
            {...(portal ? { anchor: `bottom` as const } : {})}
            className={clsx(
              portal
                ? `[--anchor-gap:10px] [--anchor-padding:--spacing(2)] isolate z-[var(--z-listbox)] w-[var(--input-width)] min-w-[var(--input-width)]`
                : `absolute inset-x-0 top-full z-[var(--z-listbox)] mt-1.5 w-full`,
              `max-h-[302px] overflow-auto rounded select-none`,
              `outline outline-transparent focus:outline-hidden`,
              `border border-primary bg-background shadow-lg`,
              `transition-opacity duration-100 ease-in data-closed:data-leave:opacity-0 data-transition:pointer-events-none`,
            )}
          >
            {filtered.length === 0 && query !== `` ? (
              <div className="px-[15px] py-[15px] text-sm text-muted select-none">
                {emptyMessage}
              </div>
            ) : (
              filtered.map((option) => (
                <Headless.ComboboxOption
                  key={getKey(option)}
                  value={option}
                  className={clsx(
                    `group/option relative flex cursor-pointer items-center gap-3`,
                    `border-t border-primary/20 px-[15px] py-[15px] first:border-t-0`,
                    `text-base/6 text-foreground sm:text-sm/6`,
                    `outline-hidden select-none`,
                    `data-focus:bg-primary data-focus:text-primary-foreground`,
                    `data-disabled:opacity-50`,
                  )}
                >
                  <span className="block min-w-0 flex-1 truncate font-normal capitalize group-data-selected/option:font-semibold">
                    {renderOption ? renderOption(option) : getLabel(option)}
                  </span>
                  <span
                    className={clsx(
                      `flex size-4 shrink-0 items-center justify-center rounded-full border border-control-border bg-background`,
                      `group-data-selected/option:border-primary group-data-selected/option:ring-1 group-data-selected/option:ring-primary`,
                      `group-data-focus/option:border-primary-foreground group-data-focus/option:bg-primary-foreground group-data-focus/option:ring-primary-foreground`,
                    )}
                  >
                    <span className="size-2 rounded-full bg-primary opacity-0 group-data-selected/option:opacity-100" />
                  </span>
                </Headless.ComboboxOption>
              ))
            )}
          </Headless.ComboboxOptions>
        </span>
      </Headless.Combobox>
      {error ? <ErrorMessage id={errorMessageId}>{error}</ErrorMessage> : null}
    </Field>
  )
}
